import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowLeftRight,
  BadgeCheck,
  Bed,
  Building2,
  Compass,
  MapPin,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CoBrokerScorePill } from "@/components/CoBrokerScorePill";
import { CoBrokerContactCard } from "@/components/CoBrokerContactCard";
import { SemanticFeatureChips } from "@/components/SemanticFeatureChips";
import { ReportListingButton } from "@/components/ReportListingButton";
import { StatusChip, ExclusiveAtsChip } from "@/components/StatusChip";
import { ActiveRadarEmpty } from "@/components/ActiveRadarEmpty";
import { SkeletonMatchRow } from "@/components/Skeleton";
import { formatDateShort, formatPhp } from "@/lib/format";
import { toast } from "sonner";

function MatchScoreBadge({ score }) {
  const isStrong = score >= 80;
  const color = isStrong ? "#35622F" : "#1B2B4B";
  return (
    <div
      className="flex flex-col items-center justify-center rounded-2xl border px-4 py-3"
      style={{
        borderColor: `${color}40`,
        backgroundColor: `${color}0F`,
        color,
      }}
      data-testid="match-score-badge"
    >
      <div className="text-3xl font-bold leading-none" data-testid="match-score-value">
        {score}
        <span className="text-lg font-semibold">%</span>
      </div>
      <div
        className="mt-1 text-[10px] font-bold uppercase tracking-wider"
        style={{ color }}
      >
        {isStrong ? "Strong match" : "Match"}
      </div>
    </div>
  );
}

export default function MatchesPage() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get("highlight");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/matches/mine");
      setMatches(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load matches"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Scroll to highlighted match if present
  useEffect(() => {
    if (!highlight || matches.length === 0) return;
    const el = document.querySelector(`[data-match-id="${highlight}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight, matches]);

  const strongCount = useMemo(
    () => matches.filter((m) => m.match_score >= 80).length,
    [matches]
  );

  return (
    <section
      className="mx-auto w-full max-w-7xl px-6 py-12 md:px-10 md:py-16 mb-rise"
      data-testid="page-matches"
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#1B2B4B]">
            Matches
          </div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            AI-scored matches
          </h1>
          <p
            className="mt-3 max-w-xl text-base leading-relaxed text-gray-500"
            data-testid="matches-count"
          >
            {loading
              ? "Loading…"
              : `${matches.length} total · ${strongCount} strong (80%+)`}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={load}
          className="mb-focus-ring h-10 rounded-xl border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          data-testid="matches-refresh-button"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {(() => {
        if (loading) {
          return (
            <div className="mt-10 space-y-5" data-testid="matches-loading-skeleton">
              <SkeletonMatchRow />
              <SkeletonMatchRow />
              <SkeletonMatchRow />
            </div>
          );
        }
        if (matches.length === 0) {
          return <ActiveRadarEmpty />;
        }
        return (
          <ul className="mt-10 grid grid-cols-1 gap-6" data-testid="matches-list">
          {matches.map((m) => {
            const isHighlighted = highlight === m.id;
            return (
              <li
                key={m.id}
                data-match-id={m.id}
                data-testid={`match-card-${m.id}`}
                className={`rounded-2xl border bg-white p-6 shadow-mb-card transition ${
                  isHighlighted
                    ? "border-[#1B2B4B] ring-4 ring-[#1B2B4B]/15"
                    : "border-gray-200"
                }`}
              >
                <div className="flex flex-col gap-6 md:flex-row md:items-stretch">
                  <div className="md:w-40 md:flex-none">
                    <MatchScoreBadge score={m.match_score} />
                  </div>

                  <div className="grid flex-1 grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        <Building2 className="h-3.5 w-3.5" />
                        Listing
                        {m.is_own_listing && (
                          <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold text-gray-500">
                            YOURS
                          </span>
                        )}
                        {!m.is_own_listing && (
                          <ReportListingButton listingId={m.listing.id} />
                        )}
                      </div>
                      <div
                        className="mt-2 text-base font-semibold text-gray-900"
                        data-testid={`match-listing-title-${m.id}`}
                      >
                        {m.listing.title}
                      </div>
                      <div
                        className="mt-1 text-lg font-bold tracking-tight text-gray-900"
                        data-testid={`match-listing-price-${m.id}`}
                      >
                        {formatPhp(m.listing.price_gross ?? m.listing.price)}
                        {m.listing.transaction_type === "For Lease" && (
                          <span className="ml-1 text-xs font-medium text-gray-500">
                            /mo
                          </span>
                        )}
                      </div>
                      {m.listing.price_per_sqm != null && (
                        <div className="text-[11px] font-medium text-gray-500">
                          {formatPhp(m.listing.price_per_sqm)} / sqm
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-gray-400" />
                          {m.listing.location}
                        </span>
                        <span className="text-gray-300">·</span>
                        <span>
                          {m.listing.property_category || m.listing.property_type}
                        </span>
                        {m.listing.bedrooms > 0 && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="inline-flex items-center gap-1">
                              <Bed className="h-3 w-3 text-gray-400" />
                              {m.listing.bedrooms} BR
                            </span>
                          </>
                        )}
                        {m.listing.floor_area_sqm != null && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span>{m.listing.floor_area_sqm} sqm FA</span>
                          </>
                        )}
                        {m.listing.parking_slots != null && m.listing.parking_slots > 0 && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span>{m.listing.parking_slots} parking</span>
                          </>
                        )}
                        {m.listing.exclusive_ats && (
                          <>
                            <span className="text-gray-300">·</span>
                            <ExclusiveAtsChip />
                          </>
                        )}
                      </div>
                      {m.listing.semantic_features && (
                        <div className="mt-2">
                          <SemanticFeatureChips
                            value={m.listing.semantic_features}
                            limit={4}
                            variant="listing-mini"
                            keyPrefix={`${m.id}-lsf`}
                          />
                        </div>
                      )}
                    </div>

                    <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        <Compass className="h-3.5 w-3.5" />
                        Requirement
                        {!m.is_own_listing && (
                          <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold text-gray-500">
                            YOURS
                          </span>
                        )}
                      </div>
                      <div
                        className="mt-2 text-base font-semibold text-gray-900"
                        data-testid={`match-requirement-location-${m.id}`}
                      >
                        {m.requirement.target_location}
                      </div>
                      <div className="mt-1 text-lg font-bold tracking-tight text-gray-900">
                        Up to {formatPhp(m.requirement.budget_max ?? m.requirement.max_budget)}
                        {m.requirement.transaction_type === "Wanted to Lease" && (
                          <span className="ml-1 text-xs font-medium text-gray-500">
                            /mo
                          </span>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        <span className="font-semibold text-gray-600">
                          {m.requirement.transaction_type || "Wanted to Buy"}
                        </span>{" "}
                        · {m.requirement.property_category || m.requirement.property_type}
                      </div>
                      {m.requirement.preferred_features?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {m.requirement.preferred_features
                            .slice(0, 4)
                            .map((f, i) => (
                              <span
                                key={`${f}-${i}`}
                                className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-gray-600"
                              >
                                {f}
                              </span>
                            ))}
                        </div>
                      )}
                      {m.requirement.semantic_features && (
                        <div className="mt-2">
                          <SemanticFeatureChips
                            value={m.requirement.semantic_features}
                            limit={3}
                            variant="requirement-mini"
                            keyPrefix={`${m.id}-rsf`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <ArrowLeftRight className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">Counterpart broker:</span>
                    <span
                      className="font-semibold text-gray-900"
                      data-testid={`match-counterpart-${m.id}`}
                    >
                      {m.counterpart.full_name}
                    </span>
                    {m.counterpart.is_verified && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#35622F]/25 bg-[#35622F]/10 px-2 py-0.5 text-[11px] font-semibold text-[#35622F]">
                        <BadgeCheck className="h-3 w-3" />
                        Verified
                      </span>
                    )}
                    <CoBrokerScorePill score={m.counterpart_score} />
                    <span className="hidden text-xs text-gray-400 sm:inline">
                      · PRC {m.counterpart.prc_license_number}
                    </span>
                  </div>
                  <div className="ml-auto flex items-center gap-3">
                    <StatusChip status={m.status} />
                    <span className="text-xs text-gray-400">
                      {formatDateShort(m.created_at)}
                    </span>
                  </div>
                </div>
                <CoBrokerContactCard match={m} />
              </li>
            );
          })}
        </ul>
        );
      })()}
    </section>
  );
}
